import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { HiExclamation } from "react-icons/hi";
import { LuChevronLeft, LuChevronRight, LuRotateCcw, LuRotateCw } from "react-icons/lu";
import toast from "react-hot-toast";

import API from "../../api";
import { capture } from "../../instrument";
import { getPrescriptionWarningLabels, isActRemovedByAction } from "../../utils";
import { LinearLoadingOverlay } from "../Loading";
import DetachablePrescriptionViewer, { DetachToggleButton } from "../DetachableViewer";
import PrescriptionViewer from "../PrescriptionViewer";
import ZoomableDocument from "../ZoomableDocument";
import { useHotkeys } from "react-hotkeys-hook";

const EMPTY_POLYGONS = [];

const AdmissionDocumentsViewer = forwardRef(({ admission, isFetchingAdmission, isLocked = false, onAdmissionUpdated, onLayoutModeChange, ProcessingStatusIcon }, ref) => {
  const activeDocTabRef = useRef(null);
  const documentsScrollRef = useRef(null);
  const pendingAnchorIdRef = useRef(null);
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [viewMode, setViewMode] = useState("attach");
  const [isDocumentLoading, setIsDocumentLoading] = useState(false);

  useEffect(() => {
    activeDocTabRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
  }, [selectedDocId]);

  useEffect(() => {
    if (documentsScrollRef.current) documentsScrollRef.current.scrollTo({ top: 0, behavior: "auto" });
    setSelectedDocId(null);
  }, [admission?._id]);

  useEffect(() => {
    onLayoutModeChange?.(viewMode);
  }, [viewMode, onLayoutModeChange]);

  const documents = useMemo(() => {
    const prescriptionDocuments = (admission?.prescriptions || [])
      .filter((prescription) => prescription.img)
      .map((prescription, index, prescriptions) => ({
        id: prescription._id,
        type: "prescription",
        title: `Ordonnance ${prescriptions.length > 1 ? index + 1 : ""}`.trim(),
        shortLabel: prescriptions.length > 1 ? `Ord. ${index + 1}` : "Ordonnance",
        badge: prescription.type === "RENEWAL" ? "Renouvellement" : null,
        img: prescription.img,
        isPdf: false,
        warningLabels: getPrescriptionWarningLabels(prescription),
      }));

    const questionDocuments = (admission?.questionRecords || [])
      .filter((questionRecord) => questionRecord.answerType === "DOCUMENT" && questionRecord.answer && questionRecord.documentUrl)
      .map((questionRecord) => ({
        id: questionRecord._id,
        type: "questionRecord",
        title: questionRecord.questionCode,
        shortLabel: questionRecord.questionCode,
        badge: null,
        img: questionRecord.documentUrl,
        isPdf: `${questionRecord.documentUrl || ""} ${questionRecord.answer || ""}`.toLowerCase().includes(".pdf"),
      }));

    return [...prescriptionDocuments, ...questionDocuments];
  }, [admission?.prescriptions, admission?.questionRecords]);

  const activeIndex = Math.max(documents.findIndex((document) => document.id === selectedDocId), 0);

  const polygonsByPrescription = useMemo(() => {
    const polygons = [];

    for (const prescription of admission?.prescriptions || []) {
      for (const act of prescription.acts || []) {
        if (!act.polygon?.length) continue;

        const pointsString = act.polygon.map((point) => `${point.x},${point.y}`).join(" ");
        const existingPolygonIndex = polygons.findIndex(
          (polygon) => polygon.pointsString === pointsString && polygon.pageNumber === act.pageNumber && polygon.prescriptionId === prescription._id,
        );

        let polygon = polygons[existingPolygonIndex];
        if (existingPolygonIndex === -1) {
          polygon = {
            pointsString,
            points: act.polygon,
            className: "",
            codes: [],
            prescriptionId: prescription._id,
            pageNumber: act.pageNumber,
          };
          polygons.push(polygon);
        }

        polygon.codes.push(...act.codes);
        polygon.ALD = polygon.ALD || act.ALD;
        polygon.removedByAction = polygon.removedByAction || isActRemovedByAction(act);
        polygon.warning = polygon.warning || act.warning || act.notInserted || (act.codes.length === 0 && !isActRemovedByAction(act));
        polygon.isHandwritten = polygon.isHandwritten || act.isHandwritten;
      }

      for (const ner of prescription.ner || []) {
        if (ner.category !== "Info") continue;

        for (const nerPolygon of ner.polygons) {
          const points = nerPolygon.map((point) => ({ x: point.X, y: point.Y }));
          const pointsString = points.map((point) => `${point.x},${point.y}`).join(" ");
          const isAlreadyDrawn = polygons.some(
            (polygon) => polygon.pointsString === pointsString && polygon.pageNumber === ner.pageNumber && polygon.prescriptionId === prescription._id,
          );
          if (isAlreadyDrawn) continue;

          polygons.push({ pointsString, points, className: "", codes: [], prescriptionId: prescription._id, pageNumber: ner.pageNumber, warning: true });
        }
      }
    }

    const polygonsByPrescriptionMap = new Map();

    for (const polygon of polygons) {
      let className = "";
      if (polygon.warning) className = "stroke-1 stroke-red-500 fill-red-500 opacity-25";
      else if (polygon.removedByAction) className = "stroke-1 stroke-red-500 fill-[url(#action-removed-stripes)] opacity-50";
      else if (polygon.isHandwritten) className = "stroke-1 stroke-orange-500 fill-orange-500 opacity-25";
      else if (polygon.ALD) className = "stroke-1 stroke-purple-500 fill-transparent opacity-50";
      else className = "stroke-1 stroke-green-500 fill-transparent opacity-50";

      const bucket = polygonsByPrescriptionMap.get(polygon.prescriptionId);
      const styledPolygon = { ...polygon, className };

      if (bucket) bucket.push(styledPolygon);
      else polygonsByPrescriptionMap.set(polygon.prescriptionId, [styledPolygon]);
    }

    return polygonsByPrescriptionMap;
  }, [admission?.prescriptions]);

  const onDocumentRotation = async (documentType, documentId, direction) => {
    if (!admission?._id) return;

    setIsDocumentLoading(true);
    try {
      const { ok, data, message } = await API.put(`/admin/admission/${admission._id}/rotate-document`, {
        documentType,
        documentId,
        direction,
      });

      if (!ok) throw new Error(message || "Erreur lors de la rotation");
      onAdmissionUpdated(data);
    } catch (error) {
      capture(error);
      toast.error(error.message || "Une erreur est survenue lors de la rotation du document");
    }

    setIsDocumentLoading(false);
  };

  const onScrollToImage = useCallback(
    (targetId) => {
      const documentId = targetId?.startsWith("prescription-") ? targetId.slice("prescription-".length) : targetId;
      const targetDocument = documents.find((candidate) => candidate.id === documentId);
      if (!targetDocument) return;

      pendingAnchorIdRef.current = targetDocument.type === "prescription" ? `prescription-${targetDocument.id}` : targetDocument.id;
      if (targetDocument === documents[activeIndex]) {
        const targetElement = document.getElementById(pendingAnchorIdRef.current);
        if (targetElement) targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
        pendingAnchorIdRef.current = null;
        return;
      }

      setSelectedDocId(targetDocument.id);
    },
    [documents, activeIndex],
  );

  useImperativeHandle(
    ref,
    () => ({
      scrollToImage: onScrollToImage,
    }),
    [onScrollToImage],
  );

  useEffect(() => {
    if (!pendingAnchorIdRef.current) return;
    const targetElement = document.getElementById(pendingAnchorIdRef.current);
    if (targetElement) targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
    pendingAnchorIdRef.current = null;
  }, [selectedDocId]);

  const onPrevDoc = () => setSelectedDocId(documents[Math.max(0, activeIndex - 1)]?.id);
  const onNextDoc = () => setSelectedDocId(documents[Math.min(documents.length - 1, activeIndex + 1)]?.id);
  useHotkeys("[", onPrevDoc, { preventDefault: true }, [documents, activeIndex]);
  useHotkeys("]", onNextDoc, { preventDefault: true }, [documents, activeIndex]);

  return (
    <DetachablePrescriptionViewer
      className={viewMode === "attach" ? "min-w-0 flex-[3] border-l border-gray-200" : undefined}
      popupTitle={`Dossier ${admission?.patient?.firstName} ${admission?.patient?.lastName}`}
      id="admission-viewer"
      onModeChange={setViewMode}
    >
      {({ mode: detachMode, toggleMode: toggleDetachMode }) => {
        const activeDocument = documents[activeIndex];
        const anchorId = activeDocument?.type === "prescription" ? `prescription-${activeDocument.id}` : activeDocument?.id;
        const bannerLabels = activeDocument?.type === "prescription" ? activeDocument.warningLabels : [];

        const rotateButtons = (document, extraClass = "") =>
          isLocked ? null : (
            <div className={`flex items-center gap-0.5 ${extraClass}`}>
              <button
                onClick={(event) => {
                  event.stopPropagation();
                  onDocumentRotation(document.type, document.id, "counter-clockwise");
                }}
                className="rounded p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                title="Tourner dans le sens antihoraire"
              >
                <LuRotateCcw className="h-4 w-4" />
              </button>
              <button
                onClick={(event) => {
                  event.stopPropagation();
                  onDocumentRotation(document.type, document.id, "clockwise");
                }}
                className="rounded p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                title="Tourner dans le sens horaire"
              >
                <LuRotateCw className="h-4 w-4" />
              </button>
            </div>
          );

        const renderDocument = (document, { heightRatio, pxHeight, showFloatingRotate } = {}) => {
          if (!document) return null;

          if (document.type === "prescription") {
            return (
              <div className="relative flex justify-center overflow-hidden">
                <PrescriptionViewer
                  id={document.id}
                  imgUrl={document.img}
                  polygons={polygonsByPrescription.get(document.id) || EMPTY_POLYGONS}
                  heightRatio={heightRatio ?? (viewMode === "attach" ? 0.7 : 0.9)}
                  key={viewMode}
                />
                {showFloatingRotate === true && (
                  <div className="absolute bottom-3 right-3 z-50 rounded-lg bg-white/90 p-0.5 shadow-sm ring-1 ring-gray-200 backdrop-blur-sm">{rotateButtons(document)}</div>
                )}
              </div>
            );
          }

          const height = pxHeight ?? (viewMode === "attach" ? window.innerHeight - 280 : window.innerHeight - 140);

          return (
            <div className="relative flex justify-center overflow-hidden">
              <ZoomableDocument key={document.id}>
                {document.isPdf === true ? (
                  <embed src={document.img} type="application/pdf" className="object-contain" style={{ height }} />
                ) : (
                  <img src={document.img} alt={document.title} className="object-contain" style={{ height }} />
                )}
              </ZoomableDocument>
              {showFloatingRotate === true && (
                <div className="absolute bottom-3 right-3 z-50 rounded-lg bg-white/90 p-0.5 shadow-sm ring-1 ring-gray-200 backdrop-blur-sm">{rotateButtons(document)}</div>
              )}
            </div>
          );
        };

        return (
          <div className={`flex flex-col bg-gray-50 ${viewMode === "attach" ? "h-full" : "h-[100vh]"}`}>
            <LinearLoadingOverlay isVisible={isDocumentLoading === true || (isFetchingAdmission === true && !admission)} />
            <div className="flex h-[38px] items-center gap-3 border-b border-gray-200 bg-white px-3">
              <div className="flex w-[120px] shrink-0 items-center gap-0.5">
                <button
                  onClick={onPrevDoc}
                  disabled={activeIndex === 0}
                  className="rounded p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
                  title="Document précédent ( [ )"
                >
                  <LuChevronLeft className="h-4 w-4" />
                </button>
                <span className="px-1 text-[11px] tabular-nums text-gray-500">
                  {documents.length === 0 ? 0 : activeIndex + 1} / {documents.length}
                </span>
                <button
                  onClick={onNextDoc}
                  disabled={activeIndex >= documents.length - 1}
                  className="rounded p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
                  title="Document suivant ( ] )"
                >
                  <LuChevronRight className="h-4 w-4" />
                </button>
              </div>

              <div className="flex min-w-0 flex-1 items-center gap-4 overflow-x-auto">
                {documents.map((document, index) => {
                  const isActive = index === activeIndex;

                  return (
                    <button
                      key={document.id}
                      ref={isActive === true ? activeDocTabRef : null}
                      onClick={() => setSelectedDocId(document.id)}
                      className={`relative max-w-[220px] shrink-0 py-2 text-sm transition-colors ${
                        isActive === true ? "border-b-2 border-primary-500 font-semibold text-primary-600" : "text-gray-500 hover:text-gray-700"
                      }`}
                      title={document.shortLabel}
                    >
                      <span className="inline-flex w-full items-center gap-1.5">
                        <span className="truncate">{document.shortLabel}</span>
                        <ProcessingStatusIcon type="FILE" referenceId={document.id} className="h-3.5 w-3.5 shrink-0" />
                        {document.badge && (
                          <span className={`meta-badge shrink-0 ${isActive === true ? "bg-primary-50 text-primary-600" : "bg-gray-100 text-gray-500"}`}>{document.badge}</span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="flex shrink-0 items-center gap-1">
                {activeDocument && rotateButtons(activeDocument)}
                {activeDocument && !isLocked && <div className="mx-1 h-4 w-px bg-gray-200" />}
                <DetachToggleButton mode={detachMode} toggleMode={toggleDetachMode} />
              </div>
            </div>

            {bannerLabels.length > 0 && (
              <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1 border-b border-gray-200 bg-white px-3 py-2 text-[11px]">
                {bannerLabels.map((label) => (
                  <span key={label} className="inline-flex items-center gap-1.5 font-semibold text-red-700">
                    <HiExclamation className="h-4 w-4 shrink-0 text-red-500" />
                    {label}
                  </span>
                ))}
              </div>
            )}

            <div ref={documentsScrollRef} className="flex flex-1 items-start justify-center overflow-auto p-6" id={anchorId}>
              {renderDocument(activeDocument, { heightRatio: 0.85 })}
            </div>
          </div>
        );
      }}
    </DetachablePrescriptionViewer>
  );
});

AdmissionDocumentsViewer.displayName = "AdmissionDocumentsViewer";

export default AdmissionDocumentsViewer;
